import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { invoicesApi, Invoice, InvoiceFilters } from '../services/invoices';
import { clientsApi, Client } from '../services/clients';
import { format, parseISO, addDays } from 'date-fns';
import { useCompany } from '../contexts/CompanyContext';
import { formatCurrency } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateStartFilter, setDateStartFilter] = useState<string>('');
  const [dateEndFilter, setDateEndFilter] = useState<string>('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [readyTimesheets, setReadyTimesheets] = useState<Array<{
    clientId: string;
    clientName: string;
    invoiceSubmissionDate: number;
    timesheets: Array<{
      id: string;
      startDate: string;
      endDate: string;
      entries: any[];
    }>;
  }>>([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [issueDate, setIssueDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [dueDate, setDueDate] = useState<string>(format(addDays(new Date(), 30), 'yyyy-MM-dd'));
  const { baseCurrency } = useCompany();

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [statusFilter, clientFilter]);

  const loadClients = async () => {
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (err: any) {
      console.error('Failed to load clients:', err);
    }
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: InvoiceFilters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter as Invoice['status'];
      }
      if (clientFilter !== 'all') {
        filters.clientId = clientFilter;
      }

      const data = await invoicesApi.getAll(filters);
      
      // Apply date filters on frontend if needed
      let filteredData = data;
      if (dateStartFilter) {
        filteredData = filteredData.filter(inv => inv.issueDate >= dateStartFilter);
      }
      if (dateEndFilter) {
        filteredData = filteredData.filter(inv => inv.issueDate <= dateEndFilter);
      }
      
      setInvoices(filteredData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  const handleViewInvoice = (invoice: Invoice) => {
    navigate(`/invoices/${invoice.id}`);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    // TODO: Open edit dialog or navigate to edit page
    console.log('Edit invoice:', invoice);
  };

  const handleDeleteInvoice = async (invoice: Invoice) => {
    if (window.confirm(`Are you sure you want to delete invoice ${invoice.invoiceNumber}?`)) {
      try {
        await invoicesApi.delete(invoice.id);
        loadInvoices();
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to delete invoice');
      }
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setClientFilter('all');
    setDateStartFilter('');
    setDateEndFilter('');
  };

  const handleOpenGenerateDialog = async () => {
    try {
      const ready = await invoicesApi.getReadyTimesheets();
      setReadyTimesheets(ready);
      setSelectedTimesheets(new Set());
      setGenerateDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ready timesheets');
    }
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setSelectedTimesheets(new Set());
  };

  const handleToggleTimesheet = (timesheetId: string) => {
    const newSelected = new Set(selectedTimesheets);
    if (newSelected.has(timesheetId)) {
      newSelected.delete(timesheetId);
    } else {
      newSelected.add(timesheetId);
    }
    setSelectedTimesheets(newSelected);
  };

  const handleSelectAll = (clientTimesheets: Array<{ id: string }>) => {
    const allSelected = clientTimesheets.every(ts => selectedTimesheets.has(ts.id));
    const newSelected = new Set(selectedTimesheets);
    
    if (allSelected) {
      clientTimesheets.forEach(ts => newSelected.delete(ts.id));
    } else {
      clientTimesheets.forEach(ts => newSelected.add(ts.id));
    }
    
    setSelectedTimesheets(newSelected);
  };

  const handleGenerateInvoices = async () => {
    if (selectedTimesheets.size === 0) {
      alert('Please select at least one timesheet to generate an invoice from.');
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      // Group selected timesheets by client
      const timesheetsByClient: { [clientId: string]: string[] } = {};
      
      for (const clientGroup of readyTimesheets) {
        const clientTimesheetIds = clientGroup.timesheets
          .filter(ts => selectedTimesheets.has(ts.id))
          .map(ts => ts.id);
        
        if (clientTimesheetIds.length > 0) {
          timesheetsByClient[clientGroup.clientId] = clientTimesheetIds;
        }
      }

      // Generate invoices - one per client, aggregating multiple timesheets if selected
      const generatedInvoices: Invoice[] = [];
      const errors: string[] = [];

      for (const [clientId, timesheetIds] of Object.entries(timesheetsByClient)) {
        try {
          let invoice: Invoice;
          
          if (timesheetIds.length === 1) {
            // Single timesheet - use the existing method
            invoice = await invoicesApi.generateFromTimesheet(
              timesheetIds[0],
              issueDate,
              dueDate
            );
          } else {
            // Multiple timesheets - use monthly invoice generation
            invoice = await invoicesApi.generateMonthlyInvoice({
              timesheetIds,
              clientId,
              issueDate,
              dueDate,
            });
          }
          
          generatedInvoices.push(invoice);
        } catch (err: any) {
          const clientName = readyTimesheets.find(cg => cg.clientId === clientId)?.clientName || clientId;
          errors.push(`${clientName}: ${err.response?.data?.message || 'Failed to generate invoice'}`);
        }
      }

      if (errors.length > 0) {
        setError(`Generated ${generatedInvoices.length} invoice(s). Errors: ${errors.join(', ')}`);
      } else {
        handleCloseGenerateDialog();
        loadInvoices();
        if (generatedInvoices.length > 0) {
          // Navigate to the first generated invoice
          navigate(`/invoices/${generatedInvoices[0].id}`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  const getTotalHours = (entries: any[]): number => {
    return entries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
  };

  if (loading && invoices.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Invoices
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ReceiptIcon />}
            onClick={handleOpenGenerateDialog}
          >
            Generate from Timesheets
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => navigate('/invoices/create')}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                value={clientFilter}
                label="Client"
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <MenuItem value="all">All Clients</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={dateStartFilter}
              onChange={(e) => setDateStartFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={dateEndFilter}
              onChange={(e) => setDateEndFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={loadInvoices}
              sx={{ height: '56px' }}
            >
              Apply Filters
            </Button>
          </Grid>
          {(statusFilter !== 'all' || clientFilter !== 'all' || dateStartFilter || dateEndFilter) && (
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="text"
                onClick={clearFilters}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Invoice Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice Number</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Issue Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No invoices found. {statusFilter !== 'all' || clientFilter !== 'all' || dateStartFilter || dateEndFilter ? 'Try adjusting your filters.' : 'Create your first invoice to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{getClientName(invoice.clientId)}</TableCell>
                  <TableCell>{format(parseISO(invoice.issueDate), 'MMM dd, yyyy')}</TableCell>
                  <TableCell>{format(parseISO(invoice.dueDate), 'MMM dd, yyyy')}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(invoice.total, invoice.currency || baseCurrency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      color={getStatusColor(invoice.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Invoice">
                      <IconButton
                        size="small"
                        onClick={() => handleViewInvoice(invoice)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {invoice.status === 'draft' && (
                      <>
                        <Tooltip title="Edit Invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleEditInvoice(invoice)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteInvoice(invoice)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {invoices.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Generate Invoices Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={handleCloseGenerateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate Invoices from Approved Timesheets</DialogTitle>
        <DialogContent>
          {readyTimesheets.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No approved timesheets ready for invoicing.
            </Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Issue Date"
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {readyTimesheets.map((clientGroup) => (
                <Box key={clientGroup.clientId} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {clientGroup.clientName}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={clientGroup.timesheets.every(ts => selectedTimesheets.has(ts.id))}
                              indeterminate={
                                clientGroup.timesheets.some(ts => selectedTimesheets.has(ts.id)) &&
                                !clientGroup.timesheets.every(ts => selectedTimesheets.has(ts.id))
                              }
                              onChange={() => handleSelectAll(clientGroup.timesheets)}
                            />
                          </TableCell>
                          <TableCell>Period</TableCell>
                          <TableCell align="right">Total Hours</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientGroup.timesheets.map((timesheet) => (
                          <TableRow key={timesheet.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedTimesheets.has(timesheet.id)}
                                onChange={() => handleToggleTimesheet(timesheet.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {format(parseISO(timesheet.startDate), 'MMM dd')} - {format(parseISO(timesheet.endDate), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell align="right">
                              {getTotalHours(timesheet.entries).toFixed(2)} hrs
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenerateDialog} disabled={generating}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateInvoices}
            variant="contained"
            disabled={generating || selectedTimesheets.size === 0 || readyTimesheets.length === 0}
          >
            {generating ? 'Generating...' : (() => {
              // Count how many clients have selected timesheets
              const clientCount = readyTimesheets.filter(cg => 
                cg.timesheets.some(ts => selectedTimesheets.has(ts.id))
              ).length;
              return `Generate ${clientCount} Invoice${clientCount !== 1 ? 's' : ''}`;
            })()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices;
